/**
 * Preview page for ?preview=results: all tiers, email capture, "What's next?".
 * Used to preview content without completing the quiz.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import type { ResultsGuide } from '../types';
import { getTierPillStyle } from '../constants';

interface ResultsPreviewPageProps {
  guide: ResultsGuide | null;
  onBack: () => void;
}

export const ResultsPreviewPage: React.FC<ResultsPreviewPageProps> = ({ guide, onBack }) => {
  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-text-secondary">Loading results content…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-container mx-auto px-6 py-12 pb-24">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-text-primary">
            Results preview (all content)
          </h1>
          <Button variant="outline" size="default" onClick={onBack}>
            Back to checklist
          </Button>
        </div>

        {/* All tiers */}
        <section className="mb-16" aria-label="All result tiers">
          <h2 className="prose max-w-none text-text-primary mb-6 text-xl font-semibold">
            Tiers (by score range)
          </h2>
          <div className="space-y-8">
            {guide.tiers.map((tier) => {
              const pillStyles = getTierPillStyle(tier.level);
              return (
                <Card key={tier.id} className="p-0 bg-white overflow-visible">
                  <div className="rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-5 pt-5 pb-4">
                      <div className="flex flex-col items-center gap-2 mb-3">
                        <span
                          className="text-xs font-semibold uppercase tracking-wider text-text-secondary"
                        >
                          {tier.scoreRange}
                        </span>
                        <Badge
                          variant="outline"
                          className={`${pillStyles.bgColor} ${pillStyles.color} border-0 text-[16px] font-semibold px-4 py-1.5`}
                        >
                          Tier {tier.level}: {tier.name} {tier.emoji}
                        </Badge>
                      </div>
                      <h3 className="prose text-text-primary text-lg font-semibold mb-2">
                        {tier.headline}
                      </h3>
                      <p className="text-text-secondary font-medium mb-4">
                        {tier.summary}
                      </p>
                      <div className="text-left">
                        <p className="font-semibold text-[#111827] mb-1 mt-0">What this means</p>
                        <p className="text-[#4b5563] leading-[1.625] mt-0 mb-0">
                          {tier.whatThisMeans}
                        </p>
                      </div>
                    </div>
                    {tier.body && (
                      <>
                        <div className="border-t border-border" aria-hidden />
                        <div className="bg-white px-5 pt-7 pb-5 results-tier-prose prose prose-slate max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ h3: 'h2' }}>
                            {tier.body}
                          </ReactMarkdown>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

      </div>

      {/* What happens next - full-width section */}
      {guide.needHelp && (
        <section
          className="w-full py-16 px-6 bg-blue-50/40"
          aria-label="Need help?"
        >
          <div className="max-w-container mx-auto">
            <h2 className="text-xl font-semibold text-text-primary mb-6">
              What&apos;s next?
            </h2>
            <div className="prose prose-slate max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0 need-help-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {guide.needHelp}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
